import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useReportMacros } from '@/hooks/use-report-macros';
import { useToast } from '@/hooks/use-toast';
import { createReportMacro, deleteReportMacro, getReportTemplates, updateReportMacro, } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MacroEditorDialog } from './MacroEditorDialog';
function MacroRow({ macro, canEdit, canDelete, onEdit, onDelete, }) {
    const { t } = useTranslation('settings');
    return (_jsxs("div", { className: "border-border bg-background/50 flex items-center justify-between rounded-md border px-3 py-2", children: [_jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [_jsx("code", { className: "bg-muted text-primary rounded px-1.5 py-0.5 font-mono text-xs", children: macro.shortcode }), _jsx("span", { className: "truncate text-sm", children: macro.label }), !macro.isPersonal && (_jsx("span", { className: "bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[10px]", children: t('reporting.system') }))] }), _jsxs("div", { className: "flex items-center gap-1", children: [canEdit && (_jsx(_Fragment, { children: _jsx(Button, { variant: "ghost", size: "sm", className: "h-7 w-7 p-0", onClick: () => onEdit(macro), children: _jsx(Pencil, { className: "h-3.5 w-3.5" }) }) })), canDelete && (_jsx(_Fragment, { children: _jsx(Button, { variant: "ghost", size: "sm", className: "text-destructive hover:text-destructive h-7 w-7 p-0", "aria-label": t('button.delete', { ns: 'common' }), onClick: () => onDelete(macro), children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) }) }))] })] }));
}
export function ReportMacroSettings() {
    const { t } = useTranslation('settings');
    const { isSuperAdmin } = useCurrentUser();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { macros: apiMacros, isLoading: macrosLoading, isError: macrosError, } = useReportMacros();
    const { data: templates = [] } = useQuery({
        queryKey: ['report-templates'],
        queryFn: getReportTemplates,
        staleTime: 5 * 60_000,
    });
    const [editingMacro, setEditingMacro] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingMacro, setDeletingMacro] = useState(null);
    const createMacroMutation = useMutation({
        mutationFn: createReportMacro,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['report-macros'] });
            setEditingMacro(null);
            setIsCreating(false);
        },
        onError: () => {
            toast({ title: t('reporting.macros.saveFailed'), variant: 'destructive' });
        },
    });
    const updateMacroMutation = useMutation({
        mutationFn: ({ id, macro }) => updateReportMacro(id, {
            shortcode: macro.shortcode,
            label: macro.label,
            content: macro.content,
            templateId: macro.templateId,
            isPersonal: macro.isPersonal,
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['report-macros'] });
            setEditingMacro(null);
            setIsCreating(false);
        },
        onError: () => {
            toast({ title: t('reporting.macros.saveFailed'), variant: 'destructive' });
        },
    });
    const deleteMacroMutation = useMutation({
        mutationFn: deleteReportMacro,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['report-macros'] });
            setDeletingMacro(null);
        },
        // Keep the confirmation dialog open on failure so the user can retry.
        onError: () => {
            toast({ title: t('reporting.macros.deleteFailed'), variant: 'destructive' });
        },
    });
    // One flat list per template scope: a macro is not tied to a report
    // section, only (optionally) to a template. The API returns them sorted by
    // shortcode.
    const globalMacros = apiMacros.filter((m) => !m.templateId);
    const templateScopedMacros = apiMacros.filter((m) => m.templateId);
    const byTemplate = useMemo(() => {
        const templatesById = new Map(templates.map((tpl) => [tpl.id, tpl]));
        const map = new Map();
        for (const macro of templateScopedMacros) {
            const tplId = macro.templateId;
            const template = templatesById.get(tplId);
            const name = template?.name ?? t('reporting.macros.unknownTemplate');
            if (!map.has(tplId)) {
                map.set(tplId, { name, macros: [] });
            }
            map.get(tplId).macros.push(macro);
        }
        return map;
    }, [templateScopedMacros, templates, t]);
    const handleSaveMacro = (macro) => {
        if (editingMacro) {
            updateMacroMutation.mutate({ id: editingMacro.id, macro });
            return;
        }
        createMacroMutation.mutate({
            shortcode: macro.shortcode,
            label: macro.label,
            content: macro.content,
            templateId: macro.templateId,
            isPersonal: macro.isPersonal,
        });
    };
    const handleConfirmDelete = () => {
        if (!deletingMacro)
            return;
        deleteMacroMutation.mutate(deletingMacro.id);
    };
    return (_jsxs("div", { className: "space-y-6 pt-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { children: _jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.macros.description') }) }), _jsxs(Button, { variant: "outline", size: "sm", onClick: () => setIsCreating(true), children: [_jsx(Plus, { className: "mr-1.5 h-3.5 w-3.5" }), t('reporting.macros.newMacro')] })] }), macrosLoading && (_jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.macros.loading') })), macrosError && (_jsx("p", { className: "text-destructive text-sm", children: t('reporting.macros.loadFailed') })), !macrosLoading && !macrosError && apiMacros.length === 0 && (_jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.macros.empty') })), apiMacros.length > 0 && (_jsxs("div", { className: "space-y-2", children: [_jsx("h3", { className: "text-muted-foreground text-xs font-semibold tracking-wider uppercase", children: t('reporting.macros.allTemplates') }), _jsx("div", { className: "space-y-1", children: globalMacros.map((macro) => (_jsx(MacroRow, { macro: macro, canEdit: macro.isPersonal || isSuperAdmin, canDelete: macro.isPersonal || isSuperAdmin, onEdit: setEditingMacro, onDelete: setDeletingMacro }, macro.id))) }), globalMacros.length === 0 && (_jsx("p", { className: "text-muted-foreground text-xs", children: t('reporting.macros.noGlobal') }))] })), Array.from(byTemplate.entries()).map(([tplId, { name, macros: tplMacros }]) => (_jsxs("div", { className: "space-y-2", children: [_jsxs("h3", { className: "text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-wider uppercase", children: [name, _jsx("span", { className: "bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[10px] font-normal", children: t('reporting.macros.templateSpecific') })] }), _jsx("div", { className: "space-y-1", children: tplMacros.map((macro) => (_jsx(MacroRow, { macro: macro, canEdit: macro.isPersonal || isSuperAdmin, canDelete: macro.isPersonal || isSuperAdmin, onEdit: setEditingMacro, onDelete: setDeletingMacro }, macro.id))) })] }, tplId))), (isCreating || editingMacro) && (_jsx(MacroEditorDialog, { macro: editingMacro, allowSystemScope: isSuperAdmin, onSave: handleSaveMacro, onClose: () => {
                    setEditingMacro(null);
                    setIsCreating(false);
                } })), deletingMacro && (_jsx(Dialog, { open: true, onOpenChange: (open) => !open && setDeletingMacro(null), children: _jsxs(DialogContent, { className: "max-w-sm", "aria-describedby": undefined, children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: t('reporting.macros.deleteTitle') }) }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('reporting.macros.deleteBody', { label: deletingMacro.label }) }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", size: "sm", onClick: () => setDeletingMacro(null), children: t('button.cancel', { ns: 'common' }) }), _jsx(Button, { variant: "destructive", size: "sm", disabled: deleteMacroMutation.isPending, onClick: handleConfirmDelete, children: t('button.delete', { ns: 'common' }) })] })] }) }))] }));
}
